"use client";

/**
 * Global Error Boundary
 *
 * This component catches errors that occur in the root layout.
 * It must include its own <html> and <body> tags since it
 * replaces the entire document when triggered.
 *
 * Note: We cannot import external components here because they
 * may rely on the root layout which has failed.
 */

import { useEffect } from "react";
import { captureException } from "@/lib/analytics";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    captureException(error, {
      digest: error.digest,
      source: "global_error_boundary",
    });
  }, [error]);

  return (
    <html lang="de">
      <body className="bg-white">
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Ein unerwarteter Fehler ist aufgetreten
            </h1>
            <p className="text-gray-600 mb-8">
              Wir haben den Fehler erfasst und arbeiten daran. Bitte versuchen Sie es erneut oder
              laden Sie die Seite neu.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={reset}
                className="px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary/85 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                Erneut versuchen
              </button>
              <button
                onClick={() => (window.location.href = "/")}
                className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300/20"
              >
                Zur Startseite
              </button>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
